// Cleansmart upsells — replaces the Selleasy app.
// Every line added through an upsell carries a hidden `_upsell` property
// (bought-together / top-rated / cart / popup) so sales can be tracked on orders.
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

const money = (cents) => {
  const currency = window.Shopify?.currency?.active || 'NZD';
  try {
    return new Intl.NumberFormat('en-NZ', { style: 'currency', currency }).format(cents / 100);
  } catch {
    return '$' + (cents / 100).toFixed(2);
  }
};

const escapeHtml = (value) =>
  String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

const sizedImage = (src, width) => {
  if (!src) return '';
  const url = src.startsWith('//') ? 'https:' + src : src;
  return url + (url.includes('?') ? '&' : '?') + 'width=' + width;
};

const cartSectionIds = () =>
  [...document.querySelectorAll('cart-items-component[data-section-id]')].map((el) => el.dataset.sectionId);

async function fetchCart() {
  const res = await fetch(window.Shopify?.routes?.root ? window.Shopify.routes.root + 'cart.js' : '/cart.js', {
    headers: { Accept: 'application/json' },
  });
  return res.json();
}

/**
 * Adds lines to the cart and tells the theme about it, so the cart drawer,
 * cart page and cart count all refresh the same way as a normal add to cart.
 */
export async function addToCart(source, lines, sourceElement = document.documentElement) {
  const root = window.Shopify?.routes?.root || '/';
  const sections = cartSectionIds();
  const deferred = CartLinesUpdateEvent.createPromise();

  sourceElement.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'upsell',
      lines: lines.map((l) => ({ merchandiseId: l.id, quantity: l.quantity || 1 })),
      promise: deferred.promise,
    })
  );

  try {
    const res = await fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: lines.map((l) => ({ id: l.id, quantity: l.quantity || 1, properties: { _upsell: source } })),
        sections: sections.join(','),
      }),
    });
    const data = await res.json();
    const cart = await fetchCart();

    if (!res.ok || data.status) {
      sourceElement.dispatchEvent(
        new CartErrorEvent({ error: data.description || data.message || 'Add to cart failed', code: 'INVALID' })
      );
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: true, source: 'cs-upsell' },
      });
      throw new Error(data.description || data.message || 'Add to cart failed');
    }

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { sections: data.sections, items: data.items, source: 'cs-upsell', upsell: source },
    });
    return data;
  } catch (error) {
    deferred.reject?.(error);
    throw error;
  }
}

/** Products to show: a hand-picked list if there is one, otherwise Shopify's own recommendations. */
async function loadProducts({ productId, handles, intent, limit, excludeIds = [] }) {
  const root = window.Shopify?.routes?.root || '/';
  let products = [];

  if (handles.length) {
    const results = await Promise.all(
      handles.map((h) =>
        fetch(`${root}products/${encodeURIComponent(h)}.js`)
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null)
      )
    );
    products = results.filter(Boolean);
  } else if (productId) {
    const url = `${root}recommendations/products.json?product_id=${productId}&limit=10&intent=${intent || 'related'}`;
    const data = await fetch(url).then((r) => (r.ok ? r.json() : { products: [] })).catch(() => ({ products: [] }));
    products = data.products || [];
    // Complementary needs setting up in Search & Discovery; fall back to related if it's empty.
    if (!products.length && intent === 'complementary') {
      return loadProducts({ productId, handles, intent: 'related', limit, excludeIds });
    }
  }

  const exclude = new Set(excludeIds.map(String));
  return products
    .filter((p) => p && p.available && !exclude.has(String(p.id)))
    .filter((p) => !(p.tags || []).some((t) => String(t).toLowerCase() === 'no-upsell'))
    .slice(0, limit);
}

const firstAvailable = (product) => product.variants.find((v) => v.available) || product.variants[0];

/** The photo for a particular size/option, falling back to the product's main photo. */
const variantImage = (product, variant) => {
  const img = variant?.featured_image;
  return (img && (img.src || img)) || product.featured_image;
};

const findVariant = (product, id) => product.variants.find((v) => String(v.id) === String(id));

const priceHtml = (v) => {
  const compare = v.compare_at_price && v.compare_at_price > v.price ? `<s>${money(v.compare_at_price)}</s>` : '';
  return `${compare} ${money(v.price)}`;
};

const pagePickedVariantId = () =>
  document.querySelector('product-form-component input[name="id"], form[action*="/cart/add"] input[name="id"]')?.value;

const variantSelect = (product, className) => {
  const variants = product.variants.filter((v) => v.available);
  if (variants.length < 2) return '';
  return `<select class="${className}" aria-label="Choose an option for ${escapeHtml(product.title)}">
    ${variants.map((v) => `<option value="${v.id}" data-price="${v.price}">${escapeHtml(v.title)} – ${money(v.price)}</option>`).join('')}
  </select>`;
};

/* ------------------------------------------------------------------ */
/* Bought together: tick boxes + one "add selected" button             */
/* ------------------------------------------------------------------ */
class CsBoughtTogether extends HTMLElement {
  async connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';

    const current = JSON.parse(this.querySelector('[data-current-product]')?.textContent || 'null');
    const products = await loadProducts({
      productId: this.dataset.productId,
      handles: (this.dataset.handles || '').split(',').filter(Boolean),
      intent: this.dataset.intent,
      limit: Number(this.dataset.limit) || 2,
      excludeIds: [this.dataset.productId],
    });

    if (!products.length) return;

    this.list = this.querySelector('[data-list]');
    this.button = this.querySelector('[data-add]');
    this.totalEl = this.querySelector('[data-total]');
    this.message = this.querySelector('[data-message]');

    const rows = [];
    if (current && this.dataset.includeCurrent === 'true') rows.push({ product: current, isCurrent: true });
    products.forEach((p) => rows.push({ product: p, isCurrent: false }));
    this.current = current;
    this.rowProducts = rows.map((r) => r.product);

    this.list.innerHTML = rows
      .map(({ product, isCurrent }, i) => {
        // "This item" starts on whichever size is picked on the page; others start on their first available option.
        const v = (isCurrent && findVariant(product, pagePickedVariantId())) || firstAvailable(product);
        return `<li class="cs-bt__row" data-row>
          <input type="checkbox" id="${this.id}-${i}" checked data-check>
          <label for="${this.id}-${i}" class="cs-bt__label">
            <img src="${sizedImage(variantImage(product, v), 160)}" alt="" width="64" height="64" loading="lazy" data-img>
            <span class="cs-bt__info">
              <span class="cs-bt__title">${isCurrent ? '<strong>This item:</strong> ' : ''}${escapeHtml(product.title)}</span>
              <span class="cs-bt__price" data-price>${priceHtml(v)}</span>
            </span>
          </label>
          ${isCurrent ? `<input type="hidden" data-variant value="${v.id}" data-price="${v.price}" data-current>` : variantSelect(product, 'cs-bt__select') || `<input type="hidden" data-variant value="${v.id}" data-price="${v.price}">`}
        </li>`;
      })
      .join('');

    this.list.querySelectorAll('select.cs-bt__select').forEach((s) => s.setAttribute('data-variant', ''));
    this.hidden = false;
    this.list.addEventListener('change', (event) => {
      const select = event.target.closest('select.cs-bt__select');
      if (select) {
        const row = select.closest('[data-row]');
        const product = this.rowProducts[[...this.list.querySelectorAll('[data-row]')].indexOf(row)];
        const variant = product && findVariant(product, select.value);
        if (variant) this.showVariant(row, product, variant);
      }
      this.update();
    });
    this.button.addEventListener('click', () => this.add());
    document.addEventListener('variant:update', () => this.syncCurrentVariant());
    // The theme announces a size/option change with Shopify's standard product-select event.
    document.addEventListener(StandardEvents.productSelect, (event) => {
      // The event's product id may be a full "gid://shopify/Product/123" — compare just the number.
      if (String(event.product?.id || '').split('/').pop() !== String(this.dataset.productId)) return;
      const variantId = event.detail?.variantId;
      if (variantId) this.syncCurrentVariant(variantId);
    });
    this.update();
  }

  /** Show the right photo and price for the chosen size/option in a row. */
  showVariant(row, product, variant) {
    if (!row) return;
    const img = row.querySelector('[data-img]');
    const src = variantImage(product, variant);
    if (img && src) img.src = sizedImage(src, 160);
    const priceEl = row.querySelector('[data-price]');
    if (priceEl) priceEl.innerHTML = priceHtml(variant);
  }

  /** Keep "this item" in step with the variant picker on the page. */
  syncCurrentVariant(variantId) {
    const pickedId = variantId || pagePickedVariantId();
    const hidden = this.querySelector('[data-current]');
    if (!pickedId || !hidden) return;
    hidden.value = pickedId;
    const variant = this.current && findVariant(this.current, pickedId);
    if (variant) {
      hidden.dataset.price = variant.price;
      this.showVariant(hidden.closest('[data-row]'), this.current, variant);
    }
    this.update();
  }

  selectedLines() {
    return [...this.querySelectorAll('[data-row]')]
      .filter((row) => row.querySelector('[data-check]').checked)
      .map((row) => {
        const input = row.querySelector('[data-variant]');
        const price = input.tagName === 'SELECT' ? input.selectedOptions[0].dataset.price : input.dataset.price;
        return { id: Number(input.value), price: Number(price), current: input.hasAttribute('data-current') };
      });
  }

  update() {
    const lines = this.selectedLines();
    const total = lines.reduce((sum, l) => sum + l.price, 0);
    this.totalEl.textContent = money(total);
    this.button.disabled = lines.length === 0;
    this.querySelectorAll('[data-row]').forEach((row) => row.classList.toggle('is-off', !row.querySelector('[data-check]').checked));
  }

  async add() {
    this.syncCurrentVariant();
    const lines = this.selectedLines();
    if (!lines.length) return;
    this.button.disabled = true;
    this.button.classList.add('is-loading');
    this.message.textContent = '';
    try {
      await addToCart('bought-together', lines, this);
      this.message.textContent = 'Added to your cart';
    } catch (e) {
      this.message.textContent = e.message;
    } finally {
      this.button.disabled = false;
      this.button.classList.remove('is-loading');
    }
  }
}

/* ------------------------------------------------------------------ */
/* Product grid with a quick "Add" button on each card                 */
/* ------------------------------------------------------------------ */
class CsUpsellGrid extends HTMLElement {
  async connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';

    const handles = (this.dataset.handles || '').split(',').filter(Boolean);
    // Skipping only applies to automatic recommendations, so the row doesn't repeat the bought-together box.
    const skip = handles.length ? 0 : Number(this.dataset.skip) || 0;
    const limit = Number(this.dataset.limit) || 4;
    let products = await loadProducts({
      productId: this.dataset.productId,
      handles,
      intent: this.dataset.intent,
      limit: limit + skip,
      excludeIds: [this.dataset.productId],
    });
    products = products.slice(skip, skip + limit);
    if (!products.length) return;

    this.querySelector('[data-list]').innerHTML = products
      .map((product) => {
        const v = firstAvailable(product);
        return `<li class="cs-grid__card">
          <a href="${escapeHtml(product.url)}" class="cs-grid__media">
            <img src="${sizedImage(variantImage(product, v), 400)}" alt="${escapeHtml(product.title)}" loading="lazy" width="300" height="300">
          </a>
          <a href="${escapeHtml(product.url)}" class="cs-grid__title">${escapeHtml(product.title)}</a>
          <span class="cs-grid__price">${priceHtml(v)}</span>
          ${variantSelect(product, 'cs-grid__select')}
          <button type="button" class="button cs-grid__add" data-variant-id="${v.id}">Add</button>
        </li>`;
      })
      .join('');

    this.hidden = false;
    this.addEventListener('change', (event) => {
      const select = event.target.closest('.cs-grid__select');
      if (!select) return;
      const card = select.closest('.cs-grid__card');
      const product = products[[...this.querySelectorAll('.cs-grid__card')].indexOf(card)];
      const variant = product && findVariant(product, select.value);
      if (!variant) return;
      const img = card.querySelector('.cs-grid__media img');
      const src = variantImage(product, variant);
      if (img && src) img.src = sizedImage(src, 400);
      const priceEl = card.querySelector('.cs-grid__price');
      if (priceEl) priceEl.innerHTML = priceHtml(variant);
    });
    this.addEventListener('click', (event) => {
      const button = event.target.closest('.cs-grid__add');
      if (!button) return;
      const select = button.parentElement.querySelector('.cs-grid__select');
      addButton(button, this.dataset.source || 'top-rated', Number(select ? select.value : button.dataset.variantId), this);
    });
  }
}

async function addButton(button, source, variantId, host) {
  const label = button.textContent;
  button.disabled = true;
  button.classList.add('is-loading');
  try {
    await addToCart(source, [{ id: variantId, quantity: 1 }], host);
    button.textContent = 'Added';
    setTimeout(() => {
      button.textContent = label;
      button.disabled = false;
    }, 2000);
  } catch (e) {
    button.textContent = 'Sold out';
  } finally {
    button.classList.remove('is-loading');
  }
}

/* ------------------------------------------------------------------ */
/* Cart upsell (server-rendered in the cart drawer / cart page)        */
/* ------------------------------------------------------------------ */
if (!window.__csCartUpsellBound) {
  window.__csCartUpsellBound = true;
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-cs-cart-add]');
    if (!button) return;
    event.preventDefault();
    const select = button.closest('[data-cs-cart-item]')?.querySelector('select');
    addButton(button, 'cart', Number(select ? select.value : button.dataset.variantId), button);
  });
}

/* ------------------------------------------------------------------ */
/* Pop-up offer after this product is added to the cart                */
/* ------------------------------------------------------------------ */
class CsAtcOffer extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';
    this.dialog = this.querySelector('dialog');
    this.triggerId = String(this.dataset.triggerProductId);
    this.offerProductId = String(this.dataset.offerProductId);

    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    this.querySelector('[data-accept]')?.addEventListener('click', () => this.accept());
    this.querySelectorAll('[data-dismiss]').forEach((b) => b.addEventListener('click', () => this.dialog.close()));
    this.dialog.addEventListener('click', (e) => e.target === this.dialog && this.dialog.close());
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
  }

  onCartUpdate = (event) => {
    if (event.action !== 'add' || event.context === 'upsell') return;
    if (this.contains(event.target)) return;
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        const fromThisProduct = String(detail?.productId || '') === this.triggerId;
        if (!fromThisProduct) return;
        // detail.items is the whole cart in the Ajax API format.
        const alreadyInCart = (detail.items || []).some((item) => String(item.product_id) === this.offerProductId);
        if (alreadyInCart) return;
        const key = 'cs-atc-offer-' + this.offerProductId;
        try {
          if (this.dataset.oncePerSession === 'true' && sessionStorage.getItem(key)) return;
          sessionStorage.setItem(key, '1');
        } catch {}
        // Let the cart drawer finish opening first, then show the offer over it.
        setTimeout(() => this.dialog.showModal(), 350);
      })
      .catch(() => {});
  };

  async accept() {
    const button = this.querySelector('[data-accept]');
    const select = this.querySelector('select');
    button.disabled = true;
    button.classList.add('is-loading');
    try {
      await addToCart('popup', [{ id: Number(select ? select.value : button.dataset.variantId), quantity: 1 }], this);
      this.dialog.close();
    } catch (e) {
      this.querySelector('[data-message]').textContent = e.message;
    } finally {
      button.disabled = false;
      button.classList.remove('is-loading');
    }
  }
}

customElements.get('cs-bought-together') || customElements.define('cs-bought-together', CsBoughtTogether);
customElements.get('cs-upsell-grid') || customElements.define('cs-upsell-grid', CsUpsellGrid);
customElements.get('cs-atc-offer') || customElements.define('cs-atc-offer', CsAtcOffer);
